import React, { HTMLAttributes, useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
// import { useRouter } from "next/router";
import { Hooks, Util } from "@xt/xtbase";
import store from "store";
import { post_order } from "api/v4/order";
import Storage from "utils/storage";
import { Checkbox } from "antd";
import XtSvg from "components/xt/svg";
import ModalAlert from "components/antd/modal/alert";
import AppInputNumber from "components/app/input/number";
import useCoinMemo from "components/pages/trade/_hook/useCoinMemo";
import useBalancesAvailable from "components/pages/trade/_hook/useBalancesAvailable";
import Option from "../option";
import Slider from "../slider";
import LoginOrRegister from "../loginOrRegister";

import styles from "./index.module.scss";

import { ClsUpDownEnum } from "store/app";
import { TradeSideEnum } from "store/trade";

const { useTranslation } = Hooks;
const { Big, getUrl } = Util;

interface Props extends HTMLAttributes<HTMLDivElement> {
  tradeSide: TradeSideEnum;
  onSuccess?: () => void;
}

const Main: React.FC<Props> = ({ className, tradeSide, onSuccess }) => {
  const t = useTranslation();

  const { name, type, currentConfig } = store.market;
  const { tradeRecent, tradeRecentOnce, orderConfirm_limit } = store.trade;
  const { isLogin } = store.user;

  const { coinQuantityUpperCase, coinPriceUpperCase, coinQuantityPrecisionMarket, coinPricePrecisionMarket } = useCoinMemo();
  const { balancesAvailable, balancesAvailableLabel } = useBalancesAvailable(tradeSide);

  const isBuy = useMemo(() => tradeSide === TradeSideEnum.buy, [tradeSide]);
  const currency = useMemo(() => {
    const ary = name.split("_");
    return isBuy ? ary[1] : ary[0];
  }, [isBuy, name]); //当前交易币种

  // const cls = useMemo(() => {
  //   return isBuy ? ClsUpDownEnum.up : ClsUpDownEnum.down;
  // }, [isBuy]); //样式

  const [loading, setLoading] = useState(false);

  const [price, setPrice] = useState("");
  const [amount, setAmount] = useState("");
  const [total, setTotal] = useState(""); //成交额

  const [isErrPrice, setIsErrPrice] = useState(false);
  const [isErrAmount, setIsErrAmount] = useState(false);

  const handleInputPrice = useCallback(
    (val) => {
      setPrice(val);
      if (!val) return setTotal("");
      if (amount) {
        setTotal(
          Big(val || 0)
            .times(amount)
            .toFixed(coinPricePrecisionMarket)
        );
      }
    },
    [amount, coinPricePrecisionMarket]
  );
  const handleInputAmount = useCallback(
    (val) => {
      setAmount(val);
      if (!val) return setTotal("");
      if (price) {
        setTotal(
          Big(val || 0)
            .times(price)
            .toFixed(coinPricePrecisionMarket, isBuy ? 3 : 0) //产品vivian需求，买入时计算值向上舍入
        );
      }
    },
    [price, coinPricePrecisionMarket, isBuy]
  );
  const handleInputTotal = useCallback(
    (val) => {
      setTotal(val);

      if (price) {
        if (val) {
          setAmount(Big(val).div(price).toFixed(coinQuantityPrecisionMarket));
        } else {
          setAmount("");
        }
      } else {
        if (amount) {
          if (val) {
            setPrice(Big(val).div(amount).toFixed(coinPricePrecisionMarket));
          }
        }
      }
    },
    [price, amount, coinPricePrecisionMarket, coinQuantityPrecisionMarket]
  );
  const handleSliderChange = useCallback(
    (val, slider) => {
      console.log("handleSliderChange", val, slider);
      let value = val;
      if (!+val && !slider) {
        value = "";
      }

      if (isBuy) {
        handleInputTotal(value);
      } else {
        handleInputAmount(value);
      }
    },
    [isBuy, handleInputTotal, handleInputAmount]
  );

  const getMaxAmount = useCallback(
    (price) => {
      if (isBuy) {
        const priceNum = +price;
        if (!priceNum) return "0";
        return Big(balancesAvailable || 0)
          .div(price)
          .toFixed(coinQuantityPrecisionMarket);
      } else {
        return Big(balancesAvailable || 0).toFixed(coinQuantityPrecisionMarket);
      }
    },
    [isBuy, balancesAvailable, coinQuantityPrecisionMarket]
  ); //获取最大买入卖出数量

  const maxAmount = useMemo(() => {
    return getMaxAmount(price);
  }, [getMaxAmount, price]); //最大买入卖出数量
  const isErrAmountMemo = useMemo(() => {
    return !!(isErrAmount || (price && +amount - +maxAmount > 0));
  }, [isErrAmount, price, amount, maxAmount]);
  const sliderValue = useMemo(() => {
    if (isBuy) {
      return total;
    } else {
      return amount;
    }
  }, [isBuy, total, amount]); //滑块值
  const sliderPoint = useMemo(() => {
    return isBuy ? coinPricePrecisionMarket : coinQuantityPrecisionMarket;
  }, [isBuy, coinPricePrecisionMarket, coinQuantityPrecisionMarket]); //滑块最大值

  const btnSubmitObj = useMemo(() => {
    const cls = isBuy ? styles.btnSubmit_buy : styles.btnSubmit_sell;
    let lab = t("trade.login2Register");
    if (isLogin) {
      lab = t(isBuy ? "trade.buy" : "trade.sell") + " " + coinQuantityUpperCase;
    }
    return {
      cls,
      lab,
    };
  }, [isLogin, isBuy, coinQuantityUpperCase]);
  const btnSubmitDisabled = useMemo(() => {
    if (!isLogin) return false;
    if (loading || !currentConfig.tradingEnabled) return true;
    return false;
  }, [isLogin, loading, currentConfig]);
  const apiResPostOrder = useCallback(() => {
    if (loading) return;
    setLoading(true);
    const data = {
      symbol: name,
      side: tradeSide,
      type: "LIMIT",
      timeInForce: "GTC",
      bizType: type,
      price: price,
      quantity: amount,
    };

    post_order({
      data,
      errorPop: true,
      successPop: true,
    })
      .then((data) => {
        console.log("success", data);
        onSuccess && onSuccess();
      })
      .catch((data) => {
        console.log("error", data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [loading, name, tradeSide, type, price, amount]);

  const handleSubmit = useCallback(() => {
    if (btnSubmitDisabled) return;
    if (!isLogin) {
      const query = "?backurl=" + encodeURIComponent(location.href);
      location.href = getUrl("/accounts/login" + query);
      return;
    }

    if (!+price) return setIsErrPrice(true);
    if (!+amount) return setIsErrAmount(true);

    if (!orderConfirm_limit) return todo();

    let checked = !orderConfirm_limit;

    ModalAlert({
      title: t("trade.orderConfirm"),
      okText: t("confirm"),
      width: 450,
      closable: true,
      onOk: (close) => {
        close();
        store.trade.updateState({ orderConfirm_limit: !checked });
        Storage.set("orderConfirm_limit", !checked);
        todo();
      },
      closeIcon: <XtSvg icon={"close"} />,
      content: (
        <div className={styles.orderConfirm}>
          <div>
            <span>{store.market.formatName(store.market.name)}</span>&nbsp;
            <span className={isBuy ? ClsUpDownEnum.up : ClsUpDownEnum.down}>{isBuy ? t("trade.buy") : t("trade.sell")}</span>
          </div>

          <div>
            <div>
              <div>{t("trade.type")}</div>
              <div>{t("trade.limitOrder")}</div>
            </div>
            <div>
              <div>{t("trade.orderPrice")}</div>
              <div>{price + " " + coinPriceUpperCase}</div>
            </div>
            <div>
              <div>{t("trade.amount")}</div>
              <div>{amount + " " + coinQuantityUpperCase}</div>
            </div>
            <div>
              <div>{t("trade.totalVol")}</div>
              <div>{total + " " + coinPriceUpperCase}</div>
            </div>
          </div>

          <div>
            <Checkbox className={styles.orderConfirmCheckbox} defaultChecked={checked} onChange={(e) => (checked = e.target.checked)}>
              {t("trade.noAlertAndSetTip")}
            </Checkbox>
          </div>
        </div>
      ),
    });

    function todo() {
      if (+amount - +maxAmount > 0) return ModalAlert(t("trade.amountBigTip"));

      //交易提示：最新成交价5%
      if (tradeRecent && tradeRecent.p) {
        if (isBuy) {
          if (+price - +tradeRecent.p * 1.05 > 0) {
            return ModalAlert.confirm({
              content: t("trade.tradeAlertBuy"),
              onOk: (clsoe) => {
                clsoe();
                apiResPostOrder();
              },
            });
          }
        } else {
          if (+price - +tradeRecent.p * 0.95 < 0) {
            return ModalAlert.confirm({
              content: t("trade.tradeAlertSell"),
              onOk: (clsoe) => {
                clsoe();
                apiResPostOrder();
              },
            });
          }
        }
      }

      apiResPostOrder();
    }
  }, [
    isLogin,
    btnSubmitDisabled,
    price,
    amount,
    maxAmount,
    isBuy,
    tradeRecent,
    apiResPostOrder,
    orderConfirm_limit,
    coinPriceUpperCase,
    coinQuantityUpperCase,
    total,
  ]);

  useEffect(() => {
    // console.log("tradeRecentOnce change", tradeRecentOnce);
    setPrice("");
    setAmount("");
    setTotal("");

    if (tradeRecentOnce) {
      const price = Big(tradeRecentOnce.p || 0).toFixed(coinPricePrecisionMarket);
      setPrice(price);
      if (tradeRecentOnce.isClick) {
        const amount = tradeRecentOnce.total || tradeRecentOnce.q;
        const amountNum = Math.min(+amount, +getMaxAmount(price));
        setAmount(Big(amountNum).toFixed(coinQuantityPrecisionMarket));
        setTotal(Big(price).times(amountNum).toFixed(coinPricePrecisionMarket));
      }
    }
  }, [tradeRecentOnce, type, tradeSide]);
  useEffect(() => {
    setIsErrPrice(false);
  }, [price]);
  useEffect(() => {
    setIsErrAmount(false);
  }, [amount]);

  return (
    <div className={cx(styles.main, className)}>
      <div className={styles.nav}>
        <div>
          <span>{t("trade.avbl")}:</span>
          {/*<span className={cls}>{balancesAvailableLabel}</span>*/}
          <span>{balancesAvailableLabel}</span>
          <span>{currency.toUpperCase()}</span>
        </div>
        <Option tradeSide={tradeSide} currency={currency} />
      </div>

      <div className={styles.content}>
        <AppInputNumber
          className={styles.input}
          prefix={t("trade.price")}
          suffix={coinPriceUpperCase}
          value={price}
          onInput={handleInputPrice}
          disabled={loading}
          isErr={isErrPrice}
          point={coinPricePrecisionMarket}
          isStepPoint={true}
        />
        <AppInputNumber
          className={styles.input}
          prefix={t("trade.amount")}
          suffix={coinQuantityUpperCase}
          value={amount}
          onInput={handleInputAmount}
          disabled={loading}
          isErr={isErrAmountMemo}
          point={coinQuantityPrecisionMarket}
          isStepPoint={true}
        />

        <Slider className={styles.slider} value={sliderValue} max={balancesAvailable} point={sliderPoint} disabled={loading} onChange={handleSliderChange} />

        <AppInputNumber
          className={styles.input}
          prefix={t("trade.totalVol")}
          suffix={coinPriceUpperCase}
          value={total}
          onInput={handleInputTotal}
          disabled={loading}
          // noBtns={true}
          point={coinPricePrecisionMarket}
          isStepPoint={true}
        />

        {isLogin ? (
          <button className={cx("btnTxt", styles.btnSubmit, btnSubmitObj.cls)} disabled={btnSubmitDisabled} onClick={handleSubmit}>
            {btnSubmitObj.lab}
          </button>
        ) : (
          <LoginOrRegister isBuy={isBuy} />
        )}
      </div>
    </div>
  );
};

export default observer(Main);
